import { BaseEditViewStore, BaasicDropdownStore } from "core/stores";
import { WoundDetailsForm } from "application/wound-order/forms";
import { observable, action } from "mobx";
import { WoundService } from "../../wound-order/services";
import { LookupService } from "common/services";
import { DiagnosisCodeService } from "../../import/services";

class WoundOrderEditWoundDetailsViewStore extends BaseEditViewStore {
    params = {
        woundId: this.rootStore.routerStore.routerState.params.woundId,
    };

    form = new WoundDetailsForm({
        onSuccess: async () => {
            await this.onSubmit();
        },
    });

    woundDepthTabItems = [
        { abrv: "fill-in", name: "Fill in" },
        { abrv: "unable-to-determine", name: "Unable to determine" },
    ];

    stageOtherTabItems = [
        { abrv: "stage", name: "Pressure Stage" },
        { abrv: "other", name: "Other" },
    ];

    @observable accessor debridementTypeItems = [];
    @observable accessor pressureStageTabItems = [];
    @observable accessor modificationTypeItems = [];
    @observable accessor thicknessTypeItems = [];
    @observable accessor depthUnknownTypeItems = [];
    @observable accessor exudateTypeItems = [];
    @observable accessor selectedWound = {};
    @observable accessor selectedWoundLocation = null;

    @observable accessor selectedPressureStageTabItemAbrv = null;
    @observable accessor selectedThicknessTypeTabItemAbrv = null;
    @observable accessor selectedModificationTypeTabItemAbrv = null;
    @observable accessor selectedDepthUnknownTypeTabItemAbrv = null;
    @observable accessor selectedDebridementTypeTabItemAbrv = null;
    @observable accessor selectedExudateTypeTabItemAbrv = null;
    @observable accessor selectedEtiologyTabItemAbrv = "pressure";
    @observable accessor selectedWoundDepthTabItemAbrv = "fill-in";
    @observable accessor selectedStageThicknessTypeTabItemAbrv = "stage";

    @observable accessor tunellingRadioValue = {
        name: "isTunneling",
        type: "bool",
        value: false,
    };

    @observable accessor underminingRadioValue = {
        name: "isUndermining",
        type: "bool",
        value: false,
    };

    constructor(rootStore) {
        const lookupService = new LookupService(
            rootStore.application.baasic.apiClient
        );

        const woundService = new WoundService(
            rootStore.application.baasic.apiClient
        );

        const id = rootStore.routerStore.routerState.params.woundId;

        super(rootStore, {
            id: id,
            name: "edit-wound-details",
            authorization: "portalSection.update",
            Form: WoundDetailsForm,
            actions: () => {
                return {
                    get: async (id) => {
                        const response = await woundService.get(id, {
                            embed: "WoundDiagnosisCodes,PressureStage,ThicknessType,ModificationType,DepthUnknownType,DebridementType,ExudateType",
                        });
                        this.selectedWound = response.data;
                        this.tunellingRadioValue.value =
                            this.selectedWound.hasTunneling;
                        this.underminingRadioValue.value =
                            this.selectedWound.hasUndermining;
                        this.form
                            .$("etiologyPressureStageId")
                            .set(this.selectedWound.etiologyPressureStageId);
                        this.form
                            .$("etiologyThicknessTypeId")
                            .set(this.selectedWound.etiologyThicknessTypeId);

                        this.setSelectedTabItem(
                            "woundLocation",
                            this.selectedWound.location
                        );
                        return response.data;
                    },
                };
            },
            FormClass: WoundDetailsForm,
        });

        this.initializeTabItems("pressureStage", this.pressureStageTabItems);
        this.initializeTabItems("modificationType", this.modificationTypeItems);
        this.initializeTabItems("thicknessType", this.thicknessTypeItems);
        this.initializeTabItems("depthUnknownType", this.depthUnknownTypeItems);
        this.initializeTabItems("debridementType", this.debridementTypeItems);
        this.initializeTabItems("exudateType", this.exudateTypeItems);

        this.thicknessType = new BaasicDropdownStore(
            {
                filterable: true,
                multi: true,
            },
            {
                fetchFunc: async () => {
                    const response = await lookupService.getLookups(
                        "thicknessType"
                    );
                    return response["thicknessType"].map((item) => ({
                        id: item.id,
                        abrv: item.abrv,
                        name: item.name,
                    }));
                },
            }
        );

        this.diagnosisCodeStore = new BaasicDropdownStore(
            {
                filterable: true,
                multi: true,
            },
            {
                fetchFunc: async (term) => {
                    const diagnosisCodeService = new DiagnosisCodeService(
                        this.rootStore.application.baasic.apiClient
                    );
                    const response =
                        await diagnosisCodeService.getDiagnosisCodes({
                            searchQuery: term,
                            rpp: 100,
                        });
                    return response["item"].map((item) => ({
                        id: item.id,
                        abrv: item.abrv,
                        name: item.name + " - " + item.abrv,
                    }));
                },
            }
        );

        this.initialize();
    }

    async initialize() {
        await this.actions.get(
            this.rootStore.routerStore.routerState.params.woundId
        );

        this.setDiagnosisCodes();
    }

    @action.bound
    async setDiagnosisCodes() {
        if (this.selectedWound.woundDiagnosisCodes) {
            this.form.$("diagnosisCodes").set(
                this.selectedWound.woundDiagnosisCodes.map((item) => ({
                    id: item.diagnosisCodeId,
                    name:
                        item.diagnosisCode.name +
                        " - " +
                        item.diagnosisCode.abrv,
                }))
            );
        }
    }

    @action.bound
    setSelectedTabItem(tabType, tabItemAbrv, itemId) {
        switch (tabType) {
            case "exudateType":
                this.selectedExudateTypeTabItemAbrv = tabItemAbrv;
                this.form.$("exudateTypeId").set(itemId);
                break;
            case "etiologyType":
                this.selectedEtiologyTabItemAbrv = tabItemAbrv;
                this.form.$("etiologyOtherDescription").set(null);
                break;
            case "pressureStage":
                this.selectedPressureStageTabItemAbrv = tabItemAbrv;
                this.form.$("etiologyPressureStageId").set(itemId);
                this.form.$("etiologyThicknessTypeId").set(null);
                break;
            case "modificationType":
                this.selectedModificationTypeTabItemAbrv = tabItemAbrv;
                this.form.$("modificationTypeId").set(itemId);
                this.form.$("debridementTypeId").set(null);
                this.selectedDebridementTypeTabItemAbrv = null;
                break;
            case "woundDepthType":
                this.selectedWoundDepthTabItemAbrv = tabItemAbrv;
                if (this.selectedWoundDepthTabItemAbrv === "fill-in") {
                    this.form.$("depthUnknownTypeId").set(null);
                }
                break;
            case "depthUnknownType":
                this.form.$("depthUnknownTypeId").set(itemId);
                this.selectedDepthUnknownTypeTabItemAbrv = tabItemAbrv;
                this.form.$("depth").set(null);
                break;
            case "debridementType":
                this.form.$("debridementTypeId").set(itemId);
                this.selectedDebridementTypeTabItemAbrv = tabItemAbrv;
                break;
            case "stageThicknessType":
                this.selectedStageThicknessTypeTabItemAbrv = tabItemAbrv;
                if (this.selectedStageThicknessTypeTabItemAbrv === "stage") {
                    this.selectedThicknessTypeTabItemAbrv = null;
                }
                if (this.selectedStageThicknessTypeTabItemAbrv === "other") {
                    this.selectedEtiologyTabItemAbrv = null;
                    this.selectedPressureStageTabItemAbrv = null;
                }
                this.form.$("etiologyOtherDescription").set(null);
                break;
            case "thicknessType":
                this.selectedThicknessTypeTabItemAbrv = tabItemAbrv;
                this.form.$("etiologyThicknessTypeId").set(itemId);
                this.form.$("etiologyPressureStageId").set(null);
                break;
            case "woundLocation":
                this.selectedWoundLocation = tabItemAbrv;
                this.form
                    .$("debridementTypeId")
                    .set(this.selectedWound.debridementTypeId);
                this.form
                    .$("etiologyPressureStageId")
                    .set(this.selectedWound.etiologyPressureStageId);
                if (this.selectedWound.pressureStage != null) {
                    this.selectedStageThicknessTypeTabItemAbrv = "stage";
                    this.selectedPressureStageTabItemAbrv =
                        this.selectedWound.pressureStage.abrv;
                }
                this.form
                    .$("etiologyThicknessTypeId")
                    .set(this.selectedWound.etiologyThicknessTypeId);
                if (this.selectedWound.thicknessType != null) {
                    this.selectedStageThicknessTypeTabItemAbrv = "other";
                    this.selectedThicknessTypeTabItemAbrv =
                        this.selectedWound.thicknessType.abrv;
                    this.selectedEtiologyTabItemAbrv = null;
                }
                this.form
                    .$("etiologyOtherDescription")
                    .set(this.selectedWound.etiologyOtherDescription);
                if (
                    this.selectedWound.etiologyOtherDescription != null &&
                    this.selectedWound.etiologyOtherDescription != ""
                ) {
                    this.selectedStageThicknessTypeTabItemAbrv = "stage";
                    this.selectedEtiologyTabItemAbrv = "other";
                }
                this.form
                .$("debridementDate")
                .set(this.selectedWound.debridementDate);            
                this.form.$("length").set(this.selectedWound.length);
                this.form.$("width").set(this.selectedWound.width);
                this.form.$("depth").set(this.selectedWound.depth);
                if (this.selectedWound.depthUnknownTypeId != null) {
                    this.selectedWoundDepthTabItemAbrv = "unable-to-determine";
                    this.form
                        .$("depthUnknownTypeId")
                        .set(this.selectedWound.depthUnknownTypeId);
                    this.selectedDepthUnknownTypeTabItemAbrv =
                        this.selectedWound.depthUnknownType.abrv;
                }
                this.form
                    .$("depthUnknownTypeId")
                    .set(this.selectedWound.depthUnknownTypeId);
                this.form
                    .$("hasTunneling")
                    .set(this.selectedWound.hasTunneling);
                this.form
                    .$("hasUndermining")
                    .set(this.selectedWound.hasUndermining);
                this.form
                    .$("tunnelingSize")
                    .set(this.selectedWound.tunnelingSize);
                this.form
                    .$("underminingSize")
                    .set(this.selectedWound.underminingSize);
                this.form
                    .$("underminingStartPosition")
                    .set(this.selectedWound.underminingStartPosition);
                this.form
                    .$("underminingEndPosition")
                    .set(this.selectedWound.underminingEndPosition);
                this.form
                    .$("tunnelingPosition")
                    .set(this.selectedWound.tunnelingPosition);
                this.form.$("location").set(this.selectedWound.location);
                this.form
                    .$("exudateTypeId")
                    .set(this.selectedWound.exudateTypeId);
                if (this.selectedWound.exudateType != null) {
                    this.selectedExudateTypeTabItemAbrv =
                        this.selectedWound.exudateType.abrv;
                }
                this.form
                    .$("woundOrderId")
                    .set(this.selectedWound.woundOrderId);
                this.form
                    .$("modificationTypeId")
                    .set(this.selectedWound.modificationTypeId);
                if (this.selectedWound.modificationType != null) {
                    this.selectedModificationTypeTabItemAbrv =
                        this.selectedWound.modificationType.abrv;
                }
                this.form
                    .$("debridementTypeId")
                    .set(this.selectedWound.debridementTypeId);
                if (this.selectedWound.debridementType != null) {
                    this.selectedDebridementTypeTabItemAbrv =
                        this.selectedWound.debridementType.abrv;
                }
                this.form
                    .$("noModificationReason")
                    .set(this.selectedWound.noModificationReason);
                this.form
                    .$("id")
                    .set(this.rootStore.routerStore.routerState.params.woundId);
                break;
        }
    }

    @action.bound
    handleTunellingEventChange(event) {
        const newValue = event.target.value === "true";
        this.tunellingRadioValue.value = newValue;

        if (!newValue) {
            this.form.$("hasTunneling").set(false);
            this.form.$("tunnelingSize").set(null);
            this.form.$("tunnelingPosition").set(null);
        }
    }

    @action.bound
    async onSubmit() {
        const woundService = new WoundService(
            this.rootStore.application.baasic.apiClient
        );
        await woundService.update(this.form.values());
        this.rootStore.routerStore.goBack();
    }

    @action.bound
    handleUnderminingEventChange(event) {
        const newValue = event.target.value === "true";
        this.underminingRadioValue.value = newValue;
        if (!newValue) {
            this.form.$("hasUndermining").set(false);
            this.form.$("underminingSize").set(null);
            this.form.$("underminingStartPosition").set(null);
            this.form.$("underminingEndPosition").set(null);
        }
    }

    async initializeTabItems(lookupType, itemsArray) {
        const lookupService = new LookupService(
            this.rootStore.application.baasic.apiClient
        );
        const items = await this.fetchTabItems(lookupService, lookupType);
        itemsArray.replace(items);
    }

    async fetchTabItems(lookupService, lookupTarget) {
        const response = await lookupService.getLookups(lookupTarget);
        return response[lookupTarget].map((item) => ({
            id: item.id,
            abrv: item.abrv,
            name: item.name,
        }));
    }
}

export default WoundOrderEditWoundDetailsViewStore;
